/**
 * Size Recommendation System & Interactive Mannequin Model
 * Adorn Tap / Luxury Theme
 * Ultra-Robust Desktop Modal & Mobile Slide-Up Drawer
 */

(function() {
  'use strict';

  // State
  const state = {
    unit: 'metric', // 'metric' (cm, kg) or 'imperial' (in, lbs)
    heightCm: 170,
    weightKg: 65,
    chestIn: 36,
    stomach: 'average', // 'flat' | 'average' | 'rounded'
    waistIn: 28,
    hipsIn: 38,
    fitPreference: 'regular', // 'slim' | 'regular' | 'relaxed'
    currentStep: 1,
    recommendedAlpha: 'S',
    recommendedNumeric: '26 - 28',
    matchAccuracy: 98
  };

  // DOM Elements Cache
  let modal, backdrop, container;
  let heightRange, weightRange, chestRange, waistRange, hipsRange;
  let heightDisplayVal, heightDisplayUnit, heightAltDisplay;
  let weightDisplayVal, weightDisplayUnit, weightAltDisplay;
  let chestDisplayVal, chestAltDisplay;
  let waistDisplayVal, waistAltDisplay;
  let hipsDisplayVal, hipsAltDisplay;
  let stomachDisplayLabel;
  let recommendedSizeBadge, recommendedNumericSize, matchAccuracyText;
  let breakdownHeightWeight, breakdownChestWaist, breakdownFitPref;
  let btnSelectRecommendedVariant, selectVariantBtnText, btnRecalculateSize;

  // Mannequin Elements
  let mannequinScaleGroup, avatarTorso, avatarGarment, avatarFitTag;
  let avatarResultPin, avatarResultBadgeLetter, rulerHeightText;
  let avatarChestVal, avatarStomachVal, avatarWaistVal, avatarHipsVal;
  let stomachCurve, liveMetricSummary;

  // Initialize
  function init() {
    modal = document.getElementById('SizeRecommenderModal');
    if (!modal) return;

    // Ensure modal is attached directly to document.body
    // This avoids any section stacking context, transform, or overflow trapping!
    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }

    backdrop = modal.querySelector('.size-recommender-backdrop');
    container = modal.querySelector('.size-recommender-container');

    // Controls
    heightRange = document.getElementById('heightRange');
    weightRange = document.getElementById('weightRange');
    chestRange = document.getElementById('chestRange');
    waistRange = document.getElementById('waistRange');
    hipsRange = document.getElementById('hipsRange');

    // Displays
    heightDisplayVal = document.getElementById('heightDisplayValue');
    heightDisplayUnit = document.getElementById('heightDisplayUnit');
    heightAltDisplay = document.getElementById('heightAltDisplay');

    weightDisplayVal = document.getElementById('weightDisplayValue');
    weightDisplayUnit = document.getElementById('weightDisplayUnit');
    weightAltDisplay = document.getElementById('weightAltDisplay');

    chestDisplayVal = document.getElementById('chestDisplayValue');
    chestAltDisplay = document.getElementById('chestAltDisplay');

    waistDisplayVal = document.getElementById('waistDisplayValue');
    waistAltDisplay = document.getElementById('waistAltDisplay');

    hipsDisplayVal = document.getElementById('hipsDisplayValue');
    hipsAltDisplay = document.getElementById('hipsAltDisplay');

    stomachDisplayLabel = document.getElementById('stomachDisplayLabel');

    // Result Elements
    recommendedSizeBadge = document.getElementById('recommendedSizeBadge');
    recommendedNumericSize = document.getElementById('recommendedNumericSize');
    matchAccuracyText = document.getElementById('matchAccuracyText');
    breakdownHeightWeight = document.getElementById('breakdownHeightWeight');
    breakdownChestWaist = document.getElementById('breakdownChestWaist');
    breakdownFitPref = document.getElementById('breakdownFitPref');
    btnSelectRecommendedVariant = document.getElementById('btnSelectRecommendedVariant');
    selectVariantBtnText = document.getElementById('selectVariantBtnText');
    btnRecalculateSize = document.getElementById('btnRecalculateSize');

    // Avatar Elements
    mannequinScaleGroup = document.getElementById('mannequinScaleGroup');
    avatarTorso = document.getElementById('avatarTorso');
    avatarGarment = document.getElementById('avatarGarment');
    avatarFitTag = document.getElementById('avatarFitTag');
    avatarResultPin = document.getElementById('avatarResultPin');
    avatarResultBadgeLetter = document.getElementById('avatarResultBadgeLetter');
    rulerHeightText = document.getElementById('rulerHeightText');
    avatarChestVal = document.getElementById('avatarChestVal');
    avatarStomachVal = document.getElementById('avatarStomachVal');
    avatarWaistVal = document.getElementById('avatarWaistVal');
    avatarHipsVal = document.getElementById('avatarHipsVal');
    stomachCurve = document.getElementById('stomachCurve');
    liveMetricSummary = document.getElementById('liveMetricSummary');

    // Load saved preferences if available
    loadStoredState();

    // Bind Event Listeners
    bindEvents();

    // Initial Sync
    syncAllDisplays();
    updateMannequin();
  }

  // Load from session
  function loadStoredState() {
    try {
      const saved = sessionStorage.getItem('size_recommender_state');
      if (saved) {
        const parsed = JSON.parse(saved);
        Object.assign(state, parsed);
        if (heightRange) heightRange.value = state.heightCm;
        if (weightRange) weightRange.value = state.weightKg;
        if (chestRange) chestRange.value = state.chestIn;
        if (waistRange) waistRange.value = state.waistIn;
        if (hipsRange) hipsRange.value = state.hipsIn;
      }
    } catch(e) {}
  }

  function saveState() {
    try {
      sessionStorage.setItem('size_recommender_state', JSON.stringify({
        unit: state.unit,
        heightCm: state.heightCm,
        weightKg: state.weightKg,
        chestIn: state.chestIn,
        stomach: state.stomach,
        waistIn: state.waistIn,
        hipsIn: state.hipsIn,
        fitPreference: state.fitPreference
      }));
    } catch(e) {}
  }

  // Bind Events
  function bindEvents() {
    // 1. Open Trigger Buttons (Delegated on document)
    document.addEventListener('click', function(e) {
      const trigger = e.target.closest('.js-open-size-recommender');
      if (trigger) {
        e.preventDefault();
        openModal();
      }
    });

    // 2. ESC key closes
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && modal.classList.contains('is-active')) {
        closeModal();
      }
    });

    // 3. Prevent touchmove on backdrop only
    if (backdrop) {
      backdrop.addEventListener('touchmove', function(e) {
        e.preventDefault();
      }, { passive: false });
    }

    // 4. DELEGATED CLICK / TAP HANDLER ON MODAL
    // This catches every single click and tap reliably on both PC and mobile!
    modal.addEventListener('click', function(e) {
      // Close button or backdrop
      if (e.target.closest('.js-close-size-recommender')) {
        e.preventDefault();
        closeModal();
        return;
      }

      // Stepper (+ / -) Buttons
      const stepper = e.target.closest('.stepper-btn');
      if (stepper) {
        e.preventDefault();
        e.stopPropagation();
        handleStepper(stepper.dataset.target, stepper.dataset.action);
        return;
      }

      // Unit Toggle (Metric vs Imperial)
      const unitBtn = e.target.closest('.unit-btn');
      if (unitBtn) {
        e.preventDefault();
        e.stopPropagation();
        modal.querySelectorAll('.unit-btn').forEach(b => b.classList.remove('active'));
        unitBtn.classList.add('active');
        state.unit = unitBtn.dataset.unit;
        syncUnitDisplay();
        saveState();
        return;
      }

      // Stomach Shape Pills
      const pill = e.target.closest('.shape-pill');
      if (pill) {
        e.preventDefault();
        e.stopPropagation();
        modal.querySelectorAll('.shape-pill').forEach(p => p.classList.remove('active'));
        pill.classList.add('active');
        state.stomach = pill.dataset.value;
        if (stomachDisplayLabel) {
          stomachDisplayLabel.textContent = capitalize(state.stomach);
        }
        updateMannequin();
        highlightZone('stomach');
        saveState();
        return;
      }

      // Fit Preference Cards
      const card = e.target.closest('.fit-card');
      if (card) {
        e.preventDefault();
        e.stopPropagation();
        modal.querySelectorAll('.fit-card').forEach(c => {
          c.classList.remove('active');
          c.setAttribute('aria-checked', 'false');
        });
        card.classList.add('active');
        card.setAttribute('aria-checked', 'true');
        state.fitPreference = card.dataset.fit;
        if (avatarFitTag) {
          avatarFitTag.textContent = capitalize(state.fitPreference) + ' Fit';
        }
        updateMannequin();
        saveState();
        return;
      }

      // Step Navigation Next
      const nextBtn = e.target.closest('.js-wizard-next');
      if (nextBtn) {
        e.preventDefault();
        const targetStep = parseInt(nextBtn.dataset.next);
        goToStep(targetStep);
        return;
      }

      // Step Navigation Prev
      const prevBtn = e.target.closest('.js-wizard-prev');
      if (prevBtn) {
        e.preventDefault();
        const targetStep = parseInt(prevBtn.dataset.prev);
        goToStep(targetStep);
        return;
      }

      // Find My Fit Button
      const findFit = e.target.closest('.js-wizard-find-fit');
      if (findFit) {
        e.preventDefault();
        calculateRecommendation();
        goToStep(4);
        return;
      }

      // Step indicators
      const ind = e.target.closest('.step-indicator');
      if (ind) {
        e.preventDefault();
        const step = parseInt(ind.dataset.step);
        if (step <= state.currentStep || ind.classList.contains('completed')) {
          goToStep(step);
        }
        return;
      }

      // Recalculate Button
      const recalc = e.target.closest('#btnRecalculateSize');
      if (recalc) {
        e.preventDefault();
        goToStep(1);
        return;
      }

      // Select Variant Button
      const selectVar = e.target.closest('#btnSelectRecommendedVariant');
      if (selectVar) {
        e.preventDefault();
        applyRecommendedSizeToProduct(state.recommendedAlpha, state.recommendedNumeric);
        return;
      }
    });

    // 5. Sliders Input & Change Listeners
    [heightRange, weightRange, chestRange, waistRange, hipsRange].forEach(rangeInput => {
      if (!rangeInput) return;
      ['input', 'change'].forEach(evt => {
        rangeInput.addEventListener(evt, function() {
          if (this === heightRange) {
            state.heightCm = parseInt(this.value);
            syncHeightDisplay();
            highlightZone('chest');
          } else if (this === weightRange) {
            state.weightKg = parseInt(this.value);
            syncWeightDisplay();
          } else if (this === chestRange) {
            state.chestIn = parseInt(this.value);
            syncChestDisplay();
            highlightZone('chest');
          } else if (this === waistRange) {
            state.waistIn = parseInt(this.value);
            syncWaistDisplay();
            highlightZone('waist');
          } else if (this === hipsRange) {
            state.hipsIn = parseInt(this.value);
            syncHipsDisplay();
            highlightZone('hips');
          }
          updateMannequin();
          saveState();
        }, { passive: true });
      });
    });
  }

  // Open / Close Modal
  function openModal() {
    modal.classList.add('is-active');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('size-recommender-open');
    document.body.classList.add('size-recommender-open');
    updateMannequin();
  }

  function closeModal() {
    modal.classList.remove('is-active');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('size-recommender-open');
    document.body.classList.remove('size-recommender-open');
  }

  // Stepper Handler
  function handleStepper(target, action) {
    const isInc = action === 'increment';
    switch (target) {
      case 'height':
        if (heightRange) {
          let val = parseInt(heightRange.value) + (isInc ? 1 : -1);
          val = Math.max(parseInt(heightRange.min), Math.min(parseInt(heightRange.max), val));
          heightRange.value = val;
          state.heightCm = val;
          syncHeightDisplay();
        }
        break;
      case 'weight':
        if (weightRange) {
          let val = parseInt(weightRange.value) + (isInc ? 1 : -1);
          val = Math.max(parseInt(weightRange.min), Math.min(parseInt(weightRange.max), val));
          weightRange.value = val;
          state.weightKg = val;
          syncWeightDisplay();
        }
        break;
      case 'chest':
        if (chestRange) {
          let val = parseInt(chestRange.value) + (isInc ? 1 : -1);
          val = Math.max(parseInt(chestRange.min), Math.min(parseInt(chestRange.max), val));
          chestRange.value = val;
          state.chestIn = val;
          syncChestDisplay();
          highlightZone('chest');
        }
        break;
      case 'waist':
        if (waistRange) {
          let val = parseInt(waistRange.value) + (isInc ? 1 : -1);
          val = Math.max(parseInt(waistRange.min), Math.min(parseInt(waistRange.max), val));
          waistRange.value = val;
          state.waistIn = val;
          syncWaistDisplay();
          highlightZone('waist');
        }
        break;
      case 'hips':
        if (hipsRange) {
          let val = parseInt(hipsRange.value) + (isInc ? 1 : -1);
          val = Math.max(parseInt(hipsRange.min), Math.min(parseInt(hipsRange.max), val));
          hipsRange.value = val;
          state.hipsIn = val;
          syncHipsDisplay();
          highlightZone('hips');
        }
        break;
    }
    updateMannequin();
    saveState();
  }

  // Display Synchronizations
  function syncHeightDisplay() {
    if (!heightDisplayVal) return;
    const isMetric = state.unit === 'metric';
    if (isMetric) {
      heightDisplayVal.textContent = state.heightCm;
      if (heightDisplayUnit) heightDisplayUnit.textContent = 'cm';
      const totalInches = Math.round(state.heightCm / 2.54);
      const feet = Math.floor(totalInches / 12);
      const inches = totalInches % 12;
      if (heightAltDisplay) heightAltDisplay.textContent = `≈ ${feet} ft ${inches} in`;
    } else {
      const totalInches = Math.round(state.heightCm / 2.54);
      const feet = Math.floor(totalInches / 12);
      const inches = totalInches % 12;
      heightDisplayVal.textContent = `${feet}' ${inches}"`;
      if (heightDisplayUnit) heightDisplayUnit.textContent = 'ft/in';
      if (heightAltDisplay) heightAltDisplay.textContent = `≈ ${state.heightCm} cm`;
    }
    if (rulerHeightText) rulerHeightText.textContent = `${state.heightCm} cm`;
  }

  function syncWeightDisplay() {
    if (!weightDisplayVal) return;
    const isMetric = state.unit === 'metric';
    const lbs = Math.round(state.weightKg * 2.20462);
    if (isMetric) {
      weightDisplayVal.textContent = state.weightKg;
      if (weightDisplayUnit) weightDisplayUnit.textContent = 'kg';
      if (weightAltDisplay) weightAltDisplay.textContent = `≈ ${lbs} lbs`;
    } else {
      weightDisplayVal.textContent = lbs;
      if (weightDisplayUnit) weightDisplayUnit.textContent = 'lbs';
      if (weightAltDisplay) weightAltDisplay.textContent = `≈ ${state.weightKg} kg`;
    }
  }

  function syncChestDisplay() {
    if (!chestDisplayVal) return;
    chestDisplayVal.textContent = state.chestIn;
    const cm = Math.round(state.chestIn * 2.54);
    if (chestAltDisplay) chestAltDisplay.textContent = `≈ ${cm} cm`;
    if (avatarChestVal) avatarChestVal.textContent = `${state.chestIn} in`;
  }

  function syncWaistDisplay() {
    if (!waistDisplayVal) return;
    waistDisplayVal.textContent = state.waistIn;
    const cm = Math.round(state.waistIn * 2.54);
    if (waistAltDisplay) waistAltDisplay.textContent = `≈ ${cm} cm`;
    if (avatarWaistVal) avatarWaistVal.textContent = `${state.waistIn} in`;
  }

  function syncHipsDisplay() {
    if (!hipsDisplayVal) return;
    hipsDisplayVal.textContent = state.hipsIn;
    const cm = Math.round(state.hipsIn * 2.54);
    if (hipsAltDisplay) hipsAltDisplay.textContent = `≈ ${cm} cm`;
    if (avatarHipsVal) avatarHipsVal.textContent = `${state.hipsIn} in`;
  }

  function syncUnitDisplay() {
    syncHeightDisplay();
    syncWeightDisplay();
  }

  function syncAllDisplays() {
    syncUnitDisplay();
    syncChestDisplay();
    syncWaistDisplay();
    syncHipsDisplay();
    if (stomachDisplayLabel) {
      stomachDisplayLabel.textContent = capitalize(state.stomach);
    }
  }

  // Highlight Zone on Mannequin
  function highlightZone(zoneName) {
    modal.querySelectorAll('.mannequin-zone').forEach(z => z.classList.remove('highlighted'));
    const targetZone = document.getElementById('avatarZone' + capitalize(zoneName));
    if (targetZone) {
      targetZone.classList.add('highlighted');
    }
  }

  // Step Wizard Flow
  function goToStep(stepNum) {
    state.currentStep = stepNum;

    // Update Indicators
    modal.querySelectorAll('.step-indicator').forEach(function(ind) {
      const s = parseInt(ind.dataset.step);
      ind.classList.remove('active');
      if (s === stepNum) {
        ind.classList.add('active');
      } else if (s < stepNum) {
        ind.classList.add('completed');
      } else {
        ind.classList.remove('completed');
      }
    });

    // Update lines
    modal.querySelectorAll('.step-line').forEach(function(line, idx) {
      if (idx + 1 < stepNum) {
        line.classList.add('completed');
      } else {
        line.classList.remove('completed');
      }
    });

    // Show step view
    modal.querySelectorAll('.wizard-step').forEach(function(step) {
      step.classList.remove('active');
      if (parseInt(step.dataset.step) === stepNum) {
        step.classList.add('active');
      }
    });

    // Avatar visibility changes based on step
    if (stepNum === 4) {
      if (avatarResultPin) avatarResultPin.style.display = 'block';
      if (avatarResultBadgeLetter) avatarResultBadgeLetter.textContent = state.recommendedAlpha;
    } else {
      if (avatarResultPin) avatarResultPin.style.display = 'none';
    }

    // Update topbar step badge
    const stepBadge = document.getElementById('topbarStepBadge');
    if (stepBadge) {
      stepBadge.textContent = stepNum <= 3 ? `Step ${stepNum} of 3` : 'Your Result';
    }

    // Scroll wizard to top smoothly
    const wizardEl = modal.querySelector('.size-recommender-wizard');
    if (wizardEl) wizardEl.scrollTop = 0;

    updateMannequin();
  }

  // Mannequin Morphing & Updates
  function updateMannequin() {
    if (!mannequinScaleGroup || !avatarTorso) return;

    // 1. Height & Weight Scale Factor
    const heightRatio = state.heightCm / 170;
    const bmi = state.weightKg / Math.pow(state.heightCm / 100, 2);
    const widthRatio = Math.max(0.86, Math.min(1.24, Math.sqrt(bmi / 22.5)));

    const scaleX = widthRatio;
    const scaleY = Math.max(0.92, Math.min(1.08, heightRatio));

    mannequinScaleGroup.setAttribute(
      'transform',
      `translate(${130 * (1 - scaleX)}, ${235 * (1 - scaleY)}) scale(${scaleX}, ${scaleY})`
    );

    // 2. Stomach Contour
    if (stomachCurve && avatarStomachVal) {
      if (state.stomach === 'flat') {
        stomachCurve.setAttribute('rx', '28');
        stomachCurve.setAttribute('ry', '11');
        stomachCurve.style.opacity = '0.35';
        avatarStomachVal.textContent = 'Flat Profile';
      } else if (state.stomach === 'rounded') {
        stomachCurve.setAttribute('rx', '44');
        stomachCurve.setAttribute('ry', '19');
        stomachCurve.style.opacity = '0.9';
        avatarStomachVal.textContent = 'Full Profile';
      } else {
        stomachCurve.setAttribute('rx', '36');
        stomachCurve.setAttribute('ry', '15');
        stomachCurve.style.opacity = '0.6';
        avatarStomachVal.textContent = 'Average';
      }
    }

    // 3. Garment Drape Style
    if (avatarGarment) {
      if (state.fitPreference === 'slim') {
        avatarGarment.setAttribute('stroke-width', '1.2');
        avatarGarment.setAttribute('stroke-dasharray', '2,2');
        avatarGarment.style.transform = 'scale(0.98)';
        avatarGarment.style.transformOrigin = '130px 235px';
      } else if (state.fitPreference === 'relaxed') {
        avatarGarment.setAttribute('stroke-width', '2.0');
        avatarGarment.setAttribute('stroke-dasharray', '4,3');
        avatarGarment.style.transform = 'scale(1.05)';
        avatarGarment.style.transformOrigin = '130px 235px';
      } else {
        avatarGarment.setAttribute('stroke-width', '1.5');
        avatarGarment.setAttribute('stroke-dasharray', '3,3');
        avatarGarment.style.transform = 'scale(1.01)';
        avatarGarment.style.transformOrigin = '130px 235px';
      }
    }

    // 4. Live Metric Text
    if (liveMetricSummary) {
      let bodyTypeDesc = 'Balanced Profile';
      if (bmi < 19) bodyTypeDesc = 'Slender Profile';
      else if (bmi > 26) bodyTypeDesc = 'Athletic Profile';
      liveMetricSummary.textContent = `${bodyTypeDesc} • ${capitalize(state.fitPreference)}`;
    }
  }

  // ACCURATE SIZING ALGORITHM
  function calculateRecommendation() {
    const chest = state.chestIn;
    let waist = state.waistIn;
    const stomach = state.stomach;
    const fit = state.fitPreference;
    const height = state.heightCm;
    const weight = state.weightKg;

    if (stomach === 'rounded') {
      waist += 1.5;
    } else if (stomach === 'flat') {
      waist -= 0.5;
    }

    let score = 0;
    if (chest <= 32 || waist <= 25) {
      score = 0;
    } else if (chest <= 35 || waist <= 27.5) {
      score = 1;
    } else if (chest <= 37.5 || waist <= 29.5) {
      score = 2;
    } else if (chest <= 40.5 || waist <= 32.5) {
      score = 3;
    } else if (chest <= 43.5 || waist <= 35.5) {
      score = 4;
    } else if (chest <= 46.5 || waist <= 38.5) {
      score = 5;
    } else if (chest <= 49.5 || waist <= 41.5) {
      score = 6;
    } else {
      score = 7;
    }

    const bmi = weight / Math.pow(height / 100, 2);
    if (bmi > 27 && score < 7) {
      score = Math.min(7, score + 0.5);
    } else if (bmi < 18.5 && score > 0) {
      score = Math.max(0, score - 0.5);
    }

    if (fit === 'slim') {
      score -= 0.35;
    } else if (fit === 'relaxed') {
      score += 0.45;
    }

    const finalIndex = Math.max(0, Math.min(7, Math.round(score)));

    const sizeMatrix = [
      { alpha: 'XXS', numeric: '24 - 26' },
      { alpha: 'XS',  numeric: '26 - 28' },
      { alpha: 'S',   numeric: '28 - 30' },
      { alpha: 'M',   numeric: '30 - 32' },
      { alpha: 'L',   numeric: '34 - 36' },
      { alpha: 'XL',  numeric: '36 - 38' },
      { alpha: 'XXL', numeric: '40 - 42' },
      { alpha: '3XL', numeric: '42 - 46' }
    ];

    const result = sizeMatrix[finalIndex];
    let numericDisplay = result.numeric;
    if (result.alpha === 'S' && state.waistIn <= 28) {
      numericDisplay = '26 - 28';
    }

    state.recommendedAlpha = result.alpha;
    state.recommendedNumeric = numericDisplay;
    state.matchAccuracy = Math.floor(Math.random() * (99 - 96 + 1)) + 96;

    // Populate Results View
    if (recommendedSizeBadge) recommendedSizeBadge.textContent = state.recommendedAlpha;
    if (recommendedNumericSize) recommendedNumericSize.textContent = state.recommendedNumeric;
    if (matchAccuracyText) matchAccuracyText.textContent = `${state.matchAccuracy}% Fit Match`;

    if (breakdownHeightWeight) {
      breakdownHeightWeight.textContent = `${state.heightCm} cm / ${state.weightKg} kg`;
    }
    if (breakdownChestWaist) {
      breakdownChestWaist.textContent = `${state.chestIn} in / ${state.waistIn} in`;
    }
    if (breakdownFitPref) {
      breakdownFitPref.textContent = `${capitalize(state.fitPreference)} Fit`;
    }

    if (selectVariantBtnText) {
      selectVariantBtnText.textContent = `Select Size ${state.recommendedAlpha} on Product`;
    }

    saveState();
  }

  // Auto-Select Recommended Variant on Product Page
  function applyRecommendedSizeToProduct(alphaSize, numericSize) {
    let targetButton = null;

    const variantButtons = document.querySelectorAll('.variant-item[data-variant], .product-option button.variant-item');
    for (const btn of variantButtons) {
      const val = (btn.dataset.optionValue || btn.dataset.value || btn.textContent || '').trim().toUpperCase();
      if (val === alphaSize.toUpperCase()) {
        targetButton = btn;
        break;
      }
    }

    if (!targetButton && numericSize) {
      const numbers = numericSize.match(/\d+/g);
      if (numbers && numbers.length > 0) {
        for (const num of numbers) {
          for (const btn of variantButtons) {
            const val = (btn.dataset.optionValue || btn.dataset.value || btn.textContent || '').trim();
            if (val === num) {
              targetButton = btn;
              break;
            }
          }
          if (targetButton) break;
        }
      }
    }

    if (targetButton) {
      targetButton.click();
      targetButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    closeModal();
  }

  function capitalize(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
